import type { FC } from 'react';
import { Fragment, useEffect } from 'react';
import cx from 'classnames';
import { MDText } from 'i18n-react';

import { MonetaryValue } from 'Components/pages/Wallet/UI/MonetaryValue';
import { applySessionReplayMask } from 'Services/datadog/datadogRum';
import { MASKED_EVENT_VALUES } from 'Services/datadog/datadogRum.const';
import type { VehicleAccountListItem } from 'Types/vehicle';

import { isFalseButNotZero } from '../../../../../../utilities/helpers';
import LocalTexts from '../../PurchasesVehicleList.texts';
import { sendPricePopoverEvents } from '../PurchasesVehicleListValueCell.helpers';

import styles from '../PurchasesVehicleListValueCell.module.scss';

const LocalT = new MDText(LocalTexts);

type FeeCollectionViewProps = Pick<
	VehicleAccountListItem,
	| 'brokerFeeAmount'
	| 'finalOfferAmount'
	| 'totalPaymentAmount'
	| 'transportFeeAmount'
	| 'mechanicalGuaranteeFeeAmount'
	| 'mechanicalGuaranteePromotionalRate'
> & { slug: string };

export const FeeCollectionView: FC<FeeCollectionViewProps> = ({
	brokerFeeAmount,
	finalOfferAmount,
	mechanicalGuaranteeFeeAmount,
	mechanicalGuaranteePromotionalRate,
	totalPaymentAmount,
	transportFeeAmount,
}) => {
	const { payments } = MASKED_EVENT_VALUES;

	const hasTrialPeriod = mechanicalGuaranteePromotionalRate === true;

	const list = [
		{
			title: `${LocalT.translate('priceBreakdownPopover.vehiclePrice')}`,
			value: (
				<MonetaryValue
					{...applySessionReplayMask({ maskedActionName: payments.vehiclePrice })}
					value={finalOfferAmount}
					variant="regular"
				/>
			),
		},
		{
			hide: !brokerFeeAmount,
			title: `${LocalT.translate('priceBreakdownPopover.motorwayFee')}`,
			value: (
				<MonetaryValue
					{...applySessionReplayMask({ maskedActionName: payments.brokerFee })}
					value={brokerFeeAmount}
					variant="regular"
				/>
			),
		},
		{
			hide: !transportFeeAmount,
			title: `${LocalT.translate('priceBreakdownPopover.transportFee')}`,
			value: (
				<MonetaryValue
					{...applySessionReplayMask({ maskedActionName: payments.transportFee })}
					value={transportFeeAmount}
					variant="regular"
				/>
			),
		},
		{
			hide: isFalseButNotZero(mechanicalGuaranteeFeeAmount),
			title: hasTrialPeriod
				? `${LocalT.translate('priceBreakdownPopover.mechanicalGuaranteeFeeTrial')}`
				: `${LocalT.translate('priceBreakdownPopover.mechanicalGuaranteeFee')}`,
			value: (
				<MonetaryValue
					{...applySessionReplayMask({ maskedActionName: payments.mechanicalGuarantee })}
					value={mechanicalGuaranteeFeeAmount}
					variant="regular"
				/>
			),
		},
		{
			title: `${LocalT.translate('priceBreakdownPopover.totalToPay')}`,
			value: (
				<MonetaryValue
					{...applySessionReplayMask({ maskedActionName: payments.totalVehiclePrice })}
					value={totalPaymentAmount}
					variant="heading5"
				/>
			),
			withSeparator: true,
		},
	];

	useEffect(() => {
		sendPricePopoverEvents();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<h5>{LocalT.translate('priceBreakdownPopover.title')}</h5>
			<ul>
				{list
					.filter(({ hide }) => !hide)
					.map((item) => (
						<Fragment key={item.title}>
							<li className={cx({ [styles.withSeparator]: item.withSeparator })}>
								<span>{item.title}</span>
								{item.value}
							</li>
						</Fragment>
					))}
			</ul>
		</>
	);
};
